import api from './api';
import type {
  AddCabAttendeesPayload,
  CabAttendanceStatus,
  CabMeetingAttendee,
  UpdateCabAttendeePayload,
} from '../types/cabAttendee';

export async function fetchCabMeetingAttendees(
  meetingId: string,
  params: {
    page?: number;
    pageSize?: number;
    search?: string;
    attendanceStatus?: string;
    reason?: string;
    sortBy?: string;
    sortDir?: 'asc' | 'desc';
  } = {}
): Promise<{ items: CabMeetingAttendee[]; total: number }> {
  const response = await api.get<{ items: CabMeetingAttendee[]; total: number }>(
    `/cab-meetings/${meetingId}/attendees`,
    {
      params: {
        page: params.page ?? 1,
        pageSize: params.pageSize ?? 25,
        search: params.search || undefined,
        attendanceStatus: params.attendanceStatus || undefined,
        reason: params.reason || undefined,
        sortBy: params.sortBy || undefined,
        sortDir: params.sortDir || undefined,
      },
    }
  );
  return response.data;
}

export async function addCabMeetingAttendees(
  meetingId: string,
  payload: AddCabAttendeesPayload
): Promise<{
  items: CabMeetingAttendee[];
  skippedDuplicates: string[];
  message?: string;
  warnings?: string[];
  invitation?: {
    invitedCount: number;
    calendarForwarded: boolean;
    emailsSent: boolean;
    failed: boolean;
  } | null;
}> {
  const response = await api.post<{
    items: CabMeetingAttendee[];
    skippedDuplicates: string[];
    message?: string;
    warnings?: string[];
    invitation?: {
      invitedCount: number;
      calendarForwarded: boolean;
      emailsSent: boolean;
      failed: boolean;
    } | null;
  }>(`/cab-meetings/${meetingId}/attendees`, payload);
  return response.data;
}

export async function getCabMeetingAttendeeById(
  meetingId: string,
  attendeeId: string
): Promise<CabMeetingAttendee> {
  const response = await api.get<CabMeetingAttendee>(
    `/cab-meetings/${meetingId}/attendees/${attendeeId}`
  );
  return response.data;
}

export async function updateCabMeetingAttendee(
  meetingId: string,
  attendeeId: string,
  payload: UpdateCabAttendeePayload
): Promise<CabMeetingAttendee> {
  const response = await api.put<CabMeetingAttendee>(
    `/cab-meetings/${meetingId}/attendees/${attendeeId}`,
    payload
  );
  return response.data;
}

export async function deleteCabMeetingAttendee(
  meetingId: string,
  attendeeId: string
): Promise<void> {
  await api.delete(`/cab-meetings/${meetingId}/attendees/${attendeeId}`);
}

/** Sends calendar/email invitations for attendees with Invite Sent = False. */
export async function sendCabMeetingInvites(
  meetingId: string,
  attendeeIds?: string[]
): Promise<{
  updated: number;
  message: string;
  invitation?: {
    invitedCount: number;
    calendarForwarded: boolean;
    emailsSent: boolean;
    failed: boolean;
  };
}> {
  const response = await api.post(
    `/cab-meetings/${meetingId}/attendees/send-invites`,
    attendeeIds && attendeeIds.length > 0 ? { attendeeIds } : {}
  );
  return response.data;
}

export async function updateAttendeeAttendanceStatus(
  meetingId: string,
  attendeeId: string,
  attendanceStatus: CabAttendanceStatus
): Promise<CabMeetingAttendee> {
  return updateCabMeetingAttendee(meetingId, attendeeId, { attendanceStatus });
}
